import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react'
import styles from './RolesPage.module.sass'
import Tabs from '../../../navigation/Tabs/Tabs'
import { identity } from 'fp-ts/es6/function'
import { useDispatch, useSelector } from 'react-redux'
import { Dispatch } from 'redux'
import { RootActions, RootState } from '../../../../redux/root'
import { rolesActions } from '../../../../redux/pages/settings/roles'
import { Table } from '../../../data-components/Table/Table/Table'
import { Permission, PermissionGroup, Role } from '../../../../responses/users'
import { Column } from '../../../data-components/Table/Column'
import Field from '../../../controls/Field/Field'
import clsx from 'clsx'
import { permissionsActions } from '../../../../redux/pages/settings/permissions'
import { Checkbox } from '../../../controls/Checkbox/Checkbox'
import { fromEquals } from 'fp-ts/es6/Eq'
import { elem } from 'fp-ts/es6/Array'
import { fromBool, snoc, toBool } from '../../../../utils/utils'
import { entityRemove } from '../../../../utils/entity-utils'
import Helmet from 'react-helmet'
import { useAllPermissions, useNavigation } from '../../../../utils/hooks'
import { PERMISSIONS } from '../../../../constants/permissions'

const tabs = ['Доступы', 'Список']
const permissionEq = fromEquals<Permission>((x, y) => x.code === y.code)

const RolesPage: FunctionComponent = () => {
    const dispatch = useDispatch<Dispatch<RootActions>>()
    const [tab, setTab] = useState(tabs[0])
    const [search, setSearch] = useState('')
    const permissionsTab: boolean = tabs.indexOf(tab) === 0
    const canEdit = useAllPermissions([PERMISSIONS.SETTINGS.ROLE_EDIT])

    const { roles, permissions } = useSelector((state: RootState) => ({
        roles: state.pages.settings.roles.data,
        permissions: state.pages.settings.permissions.data,
    }))

    const designRoles = useMemo(() => roles.filter(role => role.is_design), [roles])

    useEffect(() => {
        dispatch(rolesActions.list.request(search))
    }, [dispatch, search])

    useEffect(() => {
        dispatch(permissionsActions.list.request())
    }, [dispatch])

    const listRef = useRef(null)
    useNavigation(listRef)

    const update = (role: Role, changes: Partial<Role>) => {
        if (canEdit) {
            dispatch(rolesActions.update.request({
                ...role,
                ...changes,
            }))
        }
    }

    return <div className={styles.rolesPage}>
        <Helmet><title>Роли</title></Helmet>
        <div className={styles.header}>
            <Tabs
                items={tabs}
                renderer={identity}
                itemToKey={identity}
                onChange={setTab}
                selected={tab}
            />
        </div>
        {
            !permissionsTab && <div className={styles.actions}>
                <Field
                    value={search}
                    onChange={setSearch}
                    placeholder={'Название роли'}
                />
            </div>
        }
        <div className={styles.body}>
            <Table
                className={clsx(styles.list, { [styles.hidden]: permissionsTab })}
                data={roles}
            >
                <Column<Role>
                    headerRenderer={'Название роли'}
                    cellRenderer={role => role.title}
                />
                <Column<Role>
                    headerRenderer={'Принадлежит finplan'}
                    cellRenderer={(role) => <Checkbox
                        className={styles.checkBox}
                        disabled={fromBool(role.is_admin)}
                        value={fromBool(role.is_finplan)}
                        onChange={value => update(role, { is_finplan: toBool(value) })}
                    />}
                />
                <Column<Role>
                    headerRenderer={'Принадлежит дизайн проектам'}
                    cellRenderer={(role) => <Checkbox
                        className={styles.checkBox}
                        disabled={fromBool(role.is_admin)}
                        value={fromBool(role.is_design)}
                        onChange={value => update(role, { is_design: toBool(value) })}
                    />}
                />
            </Table>

            <div className={clsx({ [styles.hidden]: !permissionsTab })}>
                {
                    permissions.map((group: PermissionGroup) => <div key={group.id}>
                        <div className={styles.label}>{group.name}</div>
                        <Table
                            data={group.permissions}
                            className={styles.table}
                        >
                            {[
                                <Column<Permission>
                                    key={'name'}
                                    cellRenderer={permission => permission.name}
                                />,
                                ...designRoles.map((role: Role) => <Column<Permission>
                                    key={role.id}
                                    headerRenderer={role.title}
                                    cellRenderer={(permission) => <Checkbox
                                        className={styles.checkBox}
                                        disabled={fromBool(role.is_admin)}
                                        value={elem(permissionEq)(permission, role.permissions)}
                                        onChange={checked => update(role, {
                                            permissions: (checked ? snoc : entityRemove)(permission)(role.permissions)
                                        })}
                                    />}
                                />)
                            ]}
                        </Table>
                    </div>)
                }
            </div>
        </div>
    </div>
}

export default RolesPage